import {
  Account,
  AccountRejectionReasons,
  Eligible_Divorce_Account_Types,
  Eligible_Estate_Account_Types,
  Eligible_Individual_Account_Types,
  Eligible_Joint_Account_Types,
  Eligible_Joint_Account_Types_Pattern,
  Eligible_Minor_Account_Types,
  Eligible_Organization_Account_Types,
  Eligible_Roth_IRA_Account_Types,
  Eligible_Traditional_IRA_Account_Types,
  Eligible_Trust_Account_Types,
  FilteredAccount,
  FilteredClientAccount,
  GrantedRole,
  IClientAccounts,
  IClientAccountsState,
  MarginCode,
  OptionPermission,
  Position,
  ProductType,
  RegisteredRole,
  RejectedAccount,
  RestrictionType,
  ServiceAgreement,
} from '@interfaces/models/client-accounts.interface';
import {
  CashPlusMoneyMarketTickers,
  cashPlusTickers,
} from '@interfaces/models/money-market-tickers-type';
import { PermissionType } from '@interfaces/models/permission-types';
import { InternalTransferFrom } from '@interfaces/state';
import { Action, createReducer, on } from '@ngrx/store';
import { getAccountName } from '@shared/helpers';
import {
  AccountTypes,
  Eligible_Estate_Role_Types,
  Eligible_Trust_Role_Types,
  RoleTypes,
  SecurityType,
} from '@shared/models';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';

import * as ClientAccountsActions from '../../actions/client-accounts/client-accounts.actions';
import { StartOverStateCleared } from '../../actions/start-over/start-over.actions';
import { InternalTransferState } from '../internal-transfer/internal-transfer.reducer';

export * from '@interfaces/models/client-accounts.interface';
export const initialState: IClientAccountsState = {
  entity: null,
  displayedAccounts: [],
  loaded: false,
  loading: false,
};
export type SpecificAccountTypes =
  | AccountTypes.JOINT
  | AccountTypes.INDV
  | AccountTypes.TRUST
  | AccountTypes.ESTATE
  | AccountTypes.IRA
  | AccountTypes.UGMA_UTMA
  | AccountTypes.ORG;
export interface AccountTypeMapping {
  accountTypes: RegExp | Set<string>;
  verifyIsPrimaryOwner: boolean;
  excludesManaged: boolean;
  excludesMarginAndOptions: boolean;
  excludesNonBrokerage: boolean;
}

type AccountTypeMappings = {
  [key in SpecificAccountTypes]: AccountTypeMapping;
};

export const accountTypeMapping: AccountTypeMappings = {
  [AccountTypes.JOINT]: {
    accountTypes: Eligible_Joint_Account_Types_Pattern,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.INDV]: {
    accountTypes: Eligible_Individual_Account_Types,
    verifyIsPrimaryOwner: true,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.TRUST]: {
    accountTypes: Eligible_Trust_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.ESTATE]: {
    accountTypes: Eligible_Estate_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.UGMA_UTMA]: {
    accountTypes: Eligible_Minor_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.IRA]: {
    accountTypes: Eligible_Divorce_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
  [AccountTypes.ORG]: {
    accountTypes: Eligible_Organization_Account_Types,
    verifyIsPrimaryOwner: false,
    excludesManaged: true,
    excludesMarginAndOptions: true,
    excludesNonBrokerage: true,
  },
};

const clientAccountsReducer = createReducer(
  initialState,
  on(ClientAccountsActions.LoadClientAccounts, (state) => ({
    ...state,
    loading: true,
  })),
  on(ClientAccountsActions.LoadClientAccountsStop, (state) => ({
    ...state,
    loading: false,
  })),
  on(ClientAccountsActions.LoadClientAccountsFail, (state) => ({
    ...state,
    loaded: false,
    loading: false,
  })),
  on(ClientAccountsActions.LoadClientAccountsSuccess, (state, prop) => ({
    ...state,
    entity: prop.payload as IClientAccounts,
    loaded: true,
    loading: false,
  })),
  on(ClientAccountsActions.UpdateDisplayedAccounts, (state, prop) => ({
    ...state,
    displayedAccounts: prop.payload,
  })),
  on(StartOverStateCleared, () => ({ ...initialState })),
);

export function reducer(state: IClientAccountsState | undefined, action: Action) {
  return clientAccountsReducer(state, action);
}

export const getClientAccountsEntity = (state: IClientAccountsState) => state.entity;
export const getClientAccountsLoading = (state: IClientAccountsState) => state.loading;
export const getClientAccountsLoaded = (state: IClientAccountsState) => state.loaded;
export const getDisplayedAccounts = (state: IClientAccountsState) => state.displayedAccounts;

export function isClientPrimaryOwner(
  clientId: string | null,
  registeredRoles: RegisteredRole[] | null,
) {
  if (clientId && registeredRoles) {
    for (const role of registeredRoles) {
      if (clientId === role.poid.toString() && role.primaryOwner) {
        return true;
      }
    }
  }
  return false;
}

// Transact authority on a VC account is granted by either:
//   1. An Authorized Signer record in registeredRoles, or
//   2. An LAA delegation: a GLAA grant in grantedRoles
// Both qualify the account for the FROM dropdown on Org → Org transfers.
export function hasVcAccountTransactAuthority(
  clientId: string | null,
  registeredRoles: RegisteredRole[] | null | undefined,
  grantedRoles: GrantedRole[] | null | undefined,
): boolean {
  if (!clientId) return false;
  const isAuthorizedSigner = !!registeredRoles?.some(
    (role) => String(role.poid) === clientId && role.authorizedSigner === true,
  );
  if (isAuthorizedSigner) return true;
  return !!grantedRoles?.some(
    (role) => role.roleType === RoleTypes.GLAA && String(role.poid) === clientId,
  );
}

/**
 * Find an account's service agreement, preferring accountId — serviceAgreementId
 * is deprecated in the AAN response and absent on the Supergraph path.
 *
 * The two passes cannot collapse into one `accountId || serviceAgreementId` key:
 * an account carries both ids with different values, so a single key compares an
 * account id against an agreement's service agreement id and matches nothing.
 */
export function getServiceAgreement(
  account: Pick<Account, 'accountId' | 'serviceAgreementId'> | null,
  serviceAgreements: ServiceAgreement[] | null,
) {
  if (!account || !serviceAgreements) return null;

  const accountId = account.accountId?.toString();
  const serviceAgreementId = account.serviceAgreementId?.toString();

  const matchedOnAccountId = accountId
    ? serviceAgreements.find((agreement) => agreement.accountId?.toString() === accountId)
    : undefined;

  if (matchedOnAccountId) return matchedOnAccountId;

  if (!serviceAgreementId) return undefined;

  return serviceAgreements.find(
    (agreement) => agreement.serviceAgreementId?.toString() === serviceAgreementId,
  );
}

/**
 * @param account
 */
export function balanceCheck(account: Account, isTransferTo: boolean) {
  try {
    if (isTransferTo) {
      return true;
    }

    return Number(account.balance) > 0;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function nonBrokerageCheck(account: Account, excludesNonBrokerage: boolean) {
  try {
    if (excludesNonBrokerage && account.productType !== ProductType.BROKERAGE) return false;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function nonBrokerageAndNonMutualFundCheck(
  account: Account,
  excludesNonBrokerageAndNonMutualFund: boolean,
) {
  try {
    if (
      excludesNonBrokerageAndNonMutualFund &&
      account.productType !== ProductType.BROKERAGE &&
      account.productType !== ProductType.MUTUAL_FUND
    )
      return false;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function managedCheck(account: Account, excludesManaged: boolean) {
  try {
    if (excludesManaged) return !account.managed;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function cashPlusCheck(account: Account, excludeCashPlus: boolean) {
  try {
    if (excludeCashPlus) return !account.cashManagementAccount;
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

export function eligibilityRoleTypeCheck(
  accountType: AccountTypes,
  clientId: string,
  serviceAgreement?: ServiceAgreement | null,
): boolean {
  if (!serviceAgreement) return false;

  let eligibleRoles: Set<RoleTypes>;
  switch (accountType) {
    case AccountTypes.ESTATE:
      eligibleRoles = Eligible_Estate_Role_Types;
      break;
    case AccountTypes.TRUST:
      eligibleRoles = Eligible_Trust_Role_Types;
      break;
    default:
      throw new Error(`Invalid account type: ${accountType}`);
  }
  return [
    ...(serviceAgreement.registeredRoles ?? []),
    ...(serviceAgreement.grantedRoles ?? []),
  ].some((role) => String(role.poid) === clientId && isValueEligible(role.roleType, eligibleRoles));
}

export function eligibilityPermissionTypeCheck(
  clientId: string,
  serviceAgreement: ServiceAgreement | null | undefined,
) {
  if (!serviceAgreement) return false;
  return serviceAgreement.registeredRoles?.some(
    (role) => String(role.poid) === clientId && role.permissionType === PermissionType.TRNS,
  );
}

function isValueEligible(value: string, rules: Set<string> | RegExp) {
  return rules instanceof Set ? rules.has(value) : value?.match(rules);
}

export function marginAndOptionsCheck(account: Account, excludesMarginAndOptions: boolean) {
  try {
    if (
      excludesMarginAndOptions &&
      (account.marginCode === MarginCode.MARGIN ||
        (account.optionPermission && account.optionPermission !== OptionPermission.NO_PAPERS))
    ) {
      return false;
    }
    return true;
  } catch (e) {
    // Guard against bad account structure.
    return false;
  }
}

// TODO: we'll definitely want to refactor this to have far fewer params. the exclusions object is a great way to do this
// because this function is only ever called by the getEligibleAccounts function, we can assume that rejectedAcccounts will always be provided either as an array or null
// eslint-disable-next-line max-params
export function validateAccountEligibility(
  accountTypes: Set<string> | RegExp,
  clientId: string,
  account: Account,
  serviceAgreements: ServiceAgreement[],
  isTransferTo: boolean,
  verifyIsPrimaryOwner: boolean,
  exclusions: {
    managed: boolean;
    marginAndOptions: boolean;
    nonBrokerage: boolean;
    nonBrokerageAndNonMutualFund: boolean;
    cashPlus: boolean;
  },
  rejectedAccounts: RejectedAccount[] | null,
): boolean {
  const rejectedAccountSeed: RejectedAccount = {
    accountId: account.accountId,
    accountName: account.accountName,
    accountType: account.accountType,
    formattedAccountName: getAccountName(
      {
        ...account,
        accountNumber: getAccountNumber(account),
        isBrokerage: account.productType === ProductType.BROKERAGE,
      },
      false,
    ),
    accountNumber: getAccountNumber(account),
  };
  if (account.nickname) {
    rejectedAccountSeed.nickname = account.nickname;
  }
  const pushRejectedAccount = (rejectionReason: AccountRejectionReasons) => {
    rejectedAccounts?.push({
      ...rejectedAccountSeed,
      rejectionReason,
    });
  };

  const accountTypeCheck = isValueEligible(account.accountType, accountTypes);
  const nonZeroBalanceCheck = balanceCheck(account, isTransferTo);
  const nonManagedAccountCheck = managedCheck(account, exclusions.managed);
  const nonCashPlusCheck = cashPlusCheck(account, exclusions.cashPlus);
  const nonMarginAndOptionsAccountCheck = marginAndOptionsCheck(
    account,
    exclusions.marginAndOptions,
  );
  const nonBrokerageAccountCheck = nonBrokerageCheck(account, exclusions.nonBrokerage);
  const nonBrokerageAndNonMutualAccountCheck = nonBrokerageAndNonMutualFundCheck(
    account,
    exclusions.nonBrokerageAndNonMutualFund,
  );
  let hasRoleTypeEligibility: boolean = true;
  let hasPermissionTypeEligibility: boolean = true;
  const isTrustAccount = isValueEligible(account.accountType, Eligible_Trust_Account_Types);
  const isEstateAccount = isValueEligible(account.accountType, Eligible_Estate_Account_Types);
  const isMinorAccount = isValueEligible(account.accountType, Eligible_Minor_Account_Types);
  const serviceAgreement = getServiceAgreement(account, serviceAgreements);

  if (isTrustAccount || isEstateAccount) {
    const accountType: AccountTypes = isTrustAccount ? AccountTypes.TRUST : AccountTypes.ESTATE;
    hasRoleTypeEligibility = eligibilityRoleTypeCheck(accountType, clientId, serviceAgreement);
  }
  if (isMinorAccount && isTransferTo) {
    hasPermissionTypeEligibility = eligibilityPermissionTypeCheck(clientId, serviceAgreement);
  }

  switch (true) {
    case !nonZeroBalanceCheck:
      pushRejectedAccount(AccountRejectionReasons.ZERO_BALANCE);
      return false;
    case !nonManagedAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.MANAGED_ACCOUNT);
      return false;
    case !nonMarginAndOptionsAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.MARGIN_OPTIONS_ACCOUNT);
      return false;
    case !nonBrokerageAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.NON_BROKERAGE_ACCOUNT);
      return false;
    case !nonBrokerageAndNonMutualAccountCheck:
      pushRejectedAccount(AccountRejectionReasons.NON_BROKERAGE_OR_NON_MUTUAL_FUND_ACCOUNT);
      return false;
    case !nonCashPlusCheck:
      pushRejectedAccount(AccountRejectionReasons.CASH_PLUS_ACCOUNT);
      return false;
    case !accountTypeCheck:
      pushRejectedAccount(AccountRejectionReasons.ACCOUNT_TYPE);
      return false;
    case !hasRoleTypeEligibility:
      pushRejectedAccount(AccountRejectionReasons.NON_MATCHING_ROLE_TYPE);
      return false;
    case !hasPermissionTypeEligibility:
      pushRejectedAccount(AccountRejectionReasons.NON_MATCHING_PERMISSION_TYPE);
      return false;
  }

  if (!serviceAgreement) {
    pushRejectedAccount(AccountRejectionReasons.INCORRECT_SERVICE_ACCOUNT_COUNT);
    return false;
  }
  if (verifyIsPrimaryOwner) {
    const primaryOwnerCheck = isClientPrimaryOwner(clientId, serviceAgreement.registeredRoles);
    if (!primaryOwnerCheck) {
      pushRejectedAccount(AccountRejectionReasons.NOT_PRIMARY_OWNER);
      return false;
    }
  }
  return true;
}

export function trimPositions(positions: Position[]) {
  const sortedPositions: Position[] = [];
  positions.forEach((position) => {
    const shareQuantity = +position.shareQuantity;
    if (position.settlementFund) {
      if (+position.balance > 0) {
        const securityName = position.securityName + ' (SETTLEMENT FUND)';
        const settlementFund = { ...position, securityName, shareQuantity };
        sortedPositions.unshift(settlementFund);
      }
    } else {
      sortedPositions.push({ ...position, shareQuantity });
    }
  });
  return sortedPositions;
}

export function getAccountNumber(account: Account) {
  let accountNumber = '0';
  if (account.brokerageAccountNumber) {
    accountNumber = account.brokerageAccountNumber;
  } else if (account.positions.length > 0 && account.positions[0].vastAccountNumber) {
    accountNumber = account.positions[0].vastAccountNumber;
  }
  return accountNumber;
}

export function createFilteredAccount(
  account: Account,
  agreement?: ServiceAgreement | null,
): FilteredAccount {
  const accountTemplate: FilteredAccount = {
    accountId: account.accountId,
    accountName: account.accountName,
    productType: account.productType,
    serviceAgreementId: account.serviceAgreementId,
    balance: account.balance,
    accountType: account.accountType,
    registrationType: account.registrationType,
    registrationSubType: account.registrationSubType,
    accountNumber: getAccountNumber(account),
    cashManagementAccount: account.cashManagementAccount || false,
    balanceAsOfDateTime: account.balanceAsOfDateTime,
    managed: account.managed,
    marginCode: account.marginCode,
    positions: trimPositions(account.positions),
    isBrokerage: !!account.brokerageAccountNumber,
    registeredRoles: agreement ? agreement.registeredRoles : [],
    grantedRoles: agreement ? agreement.grantedRoles : [],
    restrictions: account.restrictions,
    isVanguardCharitable: account.isVanguardCharitable ?? false,
    optionPermission: account.optionPermission,
  };
  if (account.nickname) {
    accountTemplate.nickname = account.nickname;
  }
  if (account.eConsenterFirstName !== undefined) {
    accountTemplate.eConsenterFirstName = account.eConsenterFirstName;
  }
  if (account.eConsenterLastName !== undefined) {
    accountTemplate.eConsenterLastName = account.eConsenterLastName;
  }
  return accountTemplate;
}

/**
 * Filter logic should be consistent for all account types.
 * @param state
 * @param accountTypes
 * @param verifyIsPrimaryOwner
 * @param isTransferTo
 * @param excludesManaged
 * @param excludesMarginAndOptions
 * @param rejectedAccounts
 */
export const getEligibleAccounts = (
  state: IClientAccountsState,
  accountTypes: Set<string> | RegExp,
  verifyIsPrimaryOwner: boolean = true,
  isTransferTo: boolean = false,
  excludesManaged: boolean = false,
  excludesMarginAndOptions: boolean = false,
  excludesNonBrokerage: boolean = false,
  excludesNonBrokerageAndNonMutualFund: boolean = false,
  isCashPlusEnabled: boolean = false,
  rejectedAccounts: RejectedAccount[] | null = [],
) => {
  const entity = getClientAccountsEntity(state);
  const eligibleAccount: FilteredClientAccount = {
    vgClientPoid: null,
    accounts: [],
  };
  if (entity && entity.vgClientPoid && entity.accounts) {
    eligibleAccount.vgClientPoid = entity.vgClientPoid;
    entity.accounts.forEach((account) => {
      if (
        validateAccountEligibility(
          accountTypes,
          entity.vgClientPoid,
          account,
          entity.serviceAgreements,
          isTransferTo,
          verifyIsPrimaryOwner,
          {
            managed: excludesManaged,
            marginAndOptions: excludesMarginAndOptions,
            nonBrokerage: excludesNonBrokerage,
            nonBrokerageAndNonMutualFund: excludesNonBrokerageAndNonMutualFund,
            cashPlus: !isCashPlusEnabled,
          },
          rejectedAccounts,
        )
      ) {
        eligibleAccount.accounts.push(
          createFilteredAccount(account, getServiceAgreement(account, entity.serviceAgreements)),
        );
      }
    });
  }
  return eligibleAccount;
};

export const getEligibleClientAccount = (
  isIraFlow: boolean,
  iraType: string | null,
  state: IClientAccountsState,
  isTransferTo: boolean,
): FilteredClientAccount => {
  if (isIraFlow) {
    if (iraType === 'ROTH') {
      return getEligibleRothIRAAccounts(state, isTransferTo);
    } else {
      return getEligibleTraditionalIRAAccounts(state, isTransferTo);
    }
  } else {
    return getEligibleIndividualAccounts(state, isTransferTo);
  }
};

export const getEligibleJointAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
  showManagedAccounts = false,
): FilteredClientAccount => {
  return getEligibleAccounts(
    state,
    Eligible_Joint_Account_Types_Pattern,
    false,
    isTransferTo,
    !showManagedAccounts,
  );
};

export const getEligibleWROSJointAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
) => {
  return getEligibleAccounts(state, Eligible_Joint_Account_Types, false, isTransferTo);
};

export const getEligibleMinorAccounts = (state: IClientAccountsState) => {
  return getEligibleAccounts(state, Eligible_Minor_Account_Types);
};

export const getEligibleIndividualAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
) => {
  return getEligibleAccounts(state, Eligible_Individual_Account_Types, true, isTransferTo);
};

export const getEligibleTraditionalIRAAccounts = (
  state: IClientAccountsState,
  isTransferTo: boolean,
) => {
  return getEligibleAccounts(state, Eligible_Traditional_IRA_Account_Types, true, isTransferTo);
};

export const getEligibleRothIRAAccounts = (state: IClientAccountsState, isTransferTo: boolean) => {
  return getEligibleAccounts(state, Eligible_Roth_IRA_Account_Types, true, isTransferTo);
};

export const getEligibleIndividualGiftingAccounts = (
  state: IClientAccountsState,
  showManagedAccounts = false,
) => {
  return getEligibleAccounts(
    state,
    Eligible_Individual_Account_Types,
    true,
    false,
    !showManagedAccounts,
  );
};

export const getEligibleTrustGiftingAccounts = (
  state: IClientAccountsState,
  showManagedAccounts = false,
) => {
  return getEligibleAccounts(
    state,
    Eligible_Trust_Account_Types,
    false,
    false,
    !showManagedAccounts,
  );
};

export const getEligibleGiftingAccountsForMO = (
  state: IClientAccountsState,
  accountTypes: Set<string> | RegExp,
  verifyIsPrimaryOwner: boolean = true,
  showManagedAccounts = false,
): FilteredClientAccount => {
  // isTransferTo=true bypasses balanceCheck so debt accounts (balance < 0) are included —
  // the assets page shows an error banner for those instead of routing to /ineligible.
  // Post-filter removes balance=0 accounts to match existing flow behaviour.
  const result = getEligibleAccounts(
    state,
    accountTypes,
    verifyIsPrimaryOwner,
    true,
    !showManagedAccounts,
    false,
  );
  return {
    ...result,
    accounts: result.accounts.filter((account) => Number(account.balance) !== 0),
  };
};

export const getEligibleTrustInheritanceAccounts = (state: IClientAccountsState) => {
  return getEligibleAccounts(state, Eligible_Trust_Account_Types, false, false);
};

export const isSpecificAccountType = (
  accountType: AccountTypes | null,
): accountType is SpecificAccountTypes =>
  !!accountType &&
  [
    AccountTypes.JOINT,
    AccountTypes.INDV,
    AccountTypes.TRUST,
    AccountTypes.ESTATE,
    AccountTypes.UGMA_UTMA,
    AccountTypes.IRA,
    AccountTypes.ORG,
  ].includes(accountType);

export const areAllPositionsMoneyMarketFunds = (selectPositions: CostBasisPosition[]): boolean => {
  return selectPositions.every(
    (position) => position.ticker !== null && position.ticker in CashPlusMoneyMarketTickers,
  );
};

export const fromPositionsHasMoneyMarketFund = (
  internalTransferFrom: InternalTransferFrom | null,
): boolean => {
  const fromPositions = internalTransferFrom?.selectedAccount?.accountData?.positions || [];
  return fromPositions.some(
    (position) => !!position.ticker && position.ticker in CashPlusMoneyMarketTickers,
  );
};

export const areAllPositionsMutualFunds = (selectPositions: CostBasisPosition[]): boolean => {
  if (!selectPositions?.length) return false;
  return selectPositions.every((position) => position.securityType === SecurityType.MUTUAL_FUND);
};

const pushRejected = (
  rejectedAccounts: RejectedAccount[] | null | undefined,
  account: FilteredAccount,
  reason: AccountRejectionReasons,
): void => {
  rejectedAccounts?.push({
    ...account,
    formattedAccountName: getAccountName(account, false),
    rejectionReason: reason,
  });
};

// Shared toggle-off rejection helper. Callers precompute which path
// (VC / non-VC) is currently disabled; if the account falls on the disabled
// path it's pushed onto rejectedAccounts and this returns true so the caller
// can skip the account.
export const checkOrgToggleRejection = (
  account: FilteredAccount,
  rejectedAccounts: RejectedAccount[] | null | undefined,
  nonVcPathDisabled: boolean,
  vcPathDisabled: boolean,
): boolean => {
  const isVC = account.isVanguardCharitable === true;
  if (vcPathDisabled && isVC) {
    pushRejected(rejectedAccounts, account, AccountRejectionReasons.VC_TOGGLE_OFF);
    return true;
  }
  if (nonVcPathDisabled && !isVC) {
    pushRejected(rejectedAccounts, account, AccountRejectionReasons.NON_VC_TOGGLE_OFF);
    return true;
  }
  return false;
};

export const getEligibleInternalTransferFromAccounts = (
  state: IClientAccountsState,
  accountType: SpecificAccountTypes | null,
  rejectedAccounts: RejectedAccount[] = [],
  transferToAccountType: SpecificAccountTypes | null = null,
  vcOrgToOrgToggle: boolean = false,
  nonVcOrgTransferToggle: boolean = false,
): FilteredClientAccount => {
  if (!accountType) {
    const account: FilteredClientAccount = {
      vgClientPoid: null,
      accounts: [],
    };
    return account;
  }

  const mapping = accountTypeMapping[accountType];

  const filterAccounts: FilteredClientAccount = getEligibleAccounts(
    state,
    mapping.accountTypes,
    mapping.verifyIsPrimaryOwner,
    false,
    false,
    mapping.excludesMarginAndOptions,
    false,
    true,
    true,
    rejectedAccounts,
  );

  // VC filtering for ORG to non-ORG transfers: Show only non-VC accounts
  const isOrgToNonOrgTransfer =
    accountType === AccountTypes.ORG &&
    transferToAccountType !== null &&
    transferToAccountType !== AccountTypes.ORG;

  // VC transact-authority gate: only apply on ORG→ORG when toggle is ON.
  // Outside of org→org, this rule does not affect personal-account
  // eligibility.
  const shouldEnforceVcTransactAuthority =
    vcOrgToOrgToggle &&
    accountType === AccountTypes.ORG &&
    transferToAccountType === AccountTypes.ORG;

  // Toggle-off ineligibility on the FROM side.
  // - VC toggle gates VC→VC only (ORG→ORG).
  // - NON_VC toggle gates every transfer where FROM is a non-VC ORG account
  //   (any TO type). When it's off, non-VC ORG accounts aren't transferable.
  const isOrgFrom = accountType === AccountTypes.ORG;
  const isOrgToOrgContext = isOrgFrom && transferToAccountType === AccountTypes.ORG;
  const shouldRejectVCOrgAccounts =
    isOrgToOrgContext && nonVcOrgTransferToggle && !vcOrgToOrgToggle;
  const shouldRejectNonVCOrgAccounts = isOrgFrom && !nonVcOrgTransferToggle;

  const clientPoid = filterAccounts.vgClientPoid;

  filterAccounts.accounts = filterAccounts.accounts.filter((account) => {
    // Toggle-off rejection — VC or non-VC path disabled by feature toggle.
    if (
      checkOrgToggleRejection(
        account,
        rejectedAccounts,
        shouldRejectNonVCOrgAccounts,
        shouldRejectVCOrgAccounts,
      )
    ) {
      return false;
    }

    // VC filtering: For ORG to non-ORG, exclude VC accounts
    // (the account being rejected is VC and the destination is non-VC).
    if (isOrgToNonOrgTransfer && account.isVanguardCharitable === true) {
      pushRejected(rejectedAccounts, account, AccountRejectionReasons.FROM_VC_TO_NON_VC_INELIGIBLE);
      return false;
    }

    // Transact-authority gate: a VC org account is only eligible as a FROM
    // account when the current client is an Authorized Signer or holds a
    // GLAA (LAA) grant.
    if (
      shouldEnforceVcTransactAuthority &&
      account.isVanguardCharitable === true &&
      !hasVcAccountTransactAuthority(clientPoid, account.registeredRoles, account.grantedRoles)
    ) {
      pushRejected(rejectedAccounts, account, AccountRejectionReasons.NO_VC_TRANSACT_AUTHORITY);
      return false;
    }

    if (account.cashManagementAccount) {
      // filter out settlement funds
      const filteredPositions: Position[] = account.positions.filter(
        (position) => !position.settlementFund,
      );

      // check if there is at least one money market fund
      const hasMoneyMarketFund: boolean = filteredPositions.some((position) =>
        cashPlusTickers.has(position.ticker),
      );

      if (hasMoneyMarketFund) {
        // Update account positions and include the account
        account.positions = filteredPositions;
        return true;
      } else {
        // exclude the account and push to rejectedAccounts
        rejectedAccounts.push({
          accountId: account.accountId,
          accountName: account.accountName,
          accountType: account.accountType,
          formattedAccountName: getAccountName(
            {
              ...account,
              accountNumber: getAccountNumber({
                ...account,
                optionPermission: OptionPermission.NO_PAPERS,
                marginCode: MarginCode.CASH,
              }),
            },
            false,
          ),
          accountNumber: getAccountNumber({
            ...account,
            optionPermission: OptionPermission.NO_PAPERS,
            marginCode: MarginCode.CASH,
          }),
          rejectionReason: AccountRejectionReasons.ZERO_BALANCE,
        });
        return false;
      }
    }
    //include non-cash management accounts without changes
    return true;
  });
  return filterAccounts;
};

export const getEligibleInternalTransferToAccounts = (
  state: IClientAccountsState,
  accountType: SpecificAccountTypes | null,
  internalTransferFromState: InternalTransferState['transferFrom'] | null,
  selectPositions: CostBasisPosition[],
  rejectedAccounts: RejectedAccount[] | null = [],
  transferFromAccountType: SpecificAccountTypes | null = null,
  vcOrgToOrgToggle: boolean = false,
  nonVcOrgTransferToggle: boolean = false,
) => {
  if (!accountType) {
    return { accounts: [] };
  }
  // Clear prior entries — callers pass a persistent array that accumulates across selector re-runs
  if (rejectedAccounts) {
    rejectedAccounts.length = 0;
  }

  const mapping: AccountTypeMapping = accountTypeMapping[accountType];
  const eligibleToAccounts: FilteredClientAccount = getEligibleAccounts(
    state,
    mapping.accountTypes,
    mapping.verifyIsPrimaryOwner,
    true,
    false,
    mapping.excludesMarginAndOptions,
    mapping.excludesNonBrokerage,
    false,
    true,
    rejectedAccounts,
  );
  const fromAccountIsVanguardCharitable =
    internalTransferFromState?.selectedAccount?.accountData?.isVanguardCharitable;

  const fromAANAccountType = internalTransferFromState?.selectedAccount?.accountData?.accountType;

  if (
    vcOrgToOrgToggle &&
    fromAccountIsVanguardCharitable !== undefined &&
    rejectedAccounts?.length
  ) {
    const hasAccountTypeRejections = rejectedAccounts.some(
      (r) => r.rejectionReason === AccountRejectionReasons.ACCOUNT_TYPE,
    );
    if (hasAccountTypeRejections) {
      const entity = getClientAccountsEntity(state);
      const accountsById = new Map(entity?.accounts?.map((a) => [a.accountId, a]) ?? []);
      rejectedAccounts.forEach((rejected) => {
        if (rejected.rejectionReason === AccountRejectionReasons.ACCOUNT_TYPE) {
          const originalAccount = accountsById.get(rejected.accountId);
          if (
            fromAccountIsVanguardCharitable === false &&
            originalAccount?.isVanguardCharitable === true
          ) {
            rejected.rejectionReason = AccountRejectionReasons.FROM_NON_VC_TO_VC_INELIGIBLE;
          } else if (
            fromAccountIsVanguardCharitable === true &&
            originalAccount?.isVanguardCharitable !== true
          ) {
            rejected.rejectionReason = AccountRejectionReasons.FROM_VC_TO_NON_VC_INELIGIBLE;
          }
        }
      });
    }
  }

  const isOrgToOrgTransfer =
    accountType === AccountTypes.ORG && transferFromAccountType === AccountTypes.ORG;

  const isNonOrgToOrgTransfer =
    accountType === AccountTypes.ORG &&
    transferFromAccountType !== null &&
    transferFromAccountType !== AccountTypes.ORG;

  const shouldFilterVCMatch =
    vcOrgToOrgToggle && !isNonOrgToOrgTransfer && fromAccountIsVanguardCharitable !== undefined;

  // Toggle-off ineligibility on the TO side — intentionally scoped to ORG→ORG.
  // We don't gate TO-side ORG accounts when FROM is non-ORG (e.g., INDV→ORG
  // stays open regardless of toggle state).
  const isOrgToOrgContext =
    accountType === AccountTypes.ORG && transferFromAccountType === AccountTypes.ORG;
  const shouldRejectVCOrgAccounts =
    isOrgToOrgContext && nonVcOrgTransferToggle && !vcOrgToOrgToggle;
  const shouldRejectNonVCOrgAccounts =
    isOrgToOrgContext && vcOrgToOrgToggle && !nonVcOrgTransferToggle;

  // Org-to-Org account type matching: only show "To" accounts with the same accountType as "From"
  const shouldFilterOrgAccountTypeMatch = isOrgToOrgTransfer && fromAANAccountType !== undefined;

  // Cash Plus filtering condition
  const shouldFilterCashPlus = !fromPositionsHasMoneyMarketFund(internalTransferFromState);

  // ACTR restriction filtering condition
  const shouldFilterACTR = !areAllPositionsMutualFunds(selectPositions);

  eligibleToAccounts.accounts = eligibleToAccounts.accounts.filter((account) => {
    // Exclude the FROM account from TO list
    if (account.accountId === internalTransferFromState?.selectedAccount?.accountData.accountId) {
      return false;
    }

    // Toggle-off rejection — VC or non-VC path disabled by feature toggle.
    if (
      checkOrgToggleRejection(
        account,
        rejectedAccounts,
        shouldRejectNonVCOrgAccounts,
        shouldRejectVCOrgAccounts,
      )
    ) {
      return false;
    }

    // VC filtering: For non-ORG to ORG, show only non-VC accounts
    if (isNonOrgToOrgTransfer && account.isVanguardCharitable === true) {
      pushRejected(rejectedAccounts, account, AccountRejectionReasons.FROM_NON_VC_TO_VC_INELIGIBLE);
      return false;
    }
    // VC matching: ensure VC-to-VC and non-VC-to-non-VC transfers.
    // Accounts with undefined isVanguardCharitable are treated as non-VC
    if (shouldFilterVCMatch) {
      if (fromAccountIsVanguardCharitable === true && account.isVanguardCharitable !== true) {
        pushRejected(
          rejectedAccounts,
          account,
          AccountRejectionReasons.FROM_VC_TO_NON_VC_INELIGIBLE,
        );
        return false;
      }
      if (fromAccountIsVanguardCharitable === false && account.isVanguardCharitable === true) {
        pushRejected(
          rejectedAccounts,
          account,
          AccountRejectionReasons.FROM_NON_VC_TO_VC_INELIGIBLE,
        );
        return false;
      }
    }

    // Org-to-Org account type matching: only show accounts with the same accountType as the selected "From" account
    if (shouldFilterOrgAccountTypeMatch && account.accountType !== fromAANAccountType) {
      return false;
    }

    // Cash Plus filtering
    if (shouldFilterCashPlus && account.cashManagementAccount) {
      rejectedAccounts?.push({
        accountId: account.accountId,
        accountName: account.accountName,
        accountType: account.accountType,
        formattedAccountName: getAccountName(
          {
            ...account,
            accountNumber: getAccountNumber({
              ...account,
              optionPermission: OptionPermission.NO_PAPERS,
              marginCode: MarginCode.CASH,
            }),
          },
          false,
        ),
        accountNumber: getAccountNumber({
          ...account,
          optionPermission: OptionPermission.NO_PAPERS,
          marginCode: MarginCode.CASH,
        }),
        rejectionReason: AccountRejectionReasons.NON_MONEY_MARKET_POSITION_SELECTED,
      });
      return false;
    }

    // ACTR restriction filtering
    if (shouldFilterACTR) {
      const hasRestriction = account.restrictions.includes(RestrictionType.ACTR);
      if (hasRestriction) {
        rejectedAccounts?.push({
          ...account,
          formattedAccountName: getAccountName(account, false),
          rejectionReason: AccountRejectionReasons.ACTR_RESTRICTION,
        });
        return false;
      }
    }

    return true;
  });

  return eligibleToAccounts;
};
